import { Country, State, City } from 'country-state-city';

/**
 * Country / state / city lists for lead forms, keyed by full NAME.
 *
 * The country-state-city package keys everything by ISO code ("IN", "TN"),
 * but leads store names: webhooks, bulk upload and referrals all arrive as
 * names, and the state reports group on the column. These helpers translate at
 * the edge so a form never saves a code.
 *
 * Older leads saved from the list dialog carry the state's ISO code ("TN");
 * `stateName` turns one of those into the name, so editing such a lead shows
 * "Tamil Nadu" in the picker and saves it back as a name.
 */

export const DEFAULT_COUNTRY = 'India';

let _countries = null;
const countries = () => (_countries ||= Country.getAllCountries());

const countryIso = (name) => {
    if (!name) return null;
    const n = String(name).trim().toLowerCase();
    const hit = countries().find(c => c.name.toLowerCase() === n || c.isoCode.toLowerCase() === n);
    return hit?.isoCode || null;
};

const statesOf = (countryName) => {
    const iso = countryIso(countryName || DEFAULT_COUNTRY);
    return iso ? State.getStatesOfCountry(iso) : [];
};

const findState = (countryName, value) => {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    return statesOf(countryName).find(s => s.name.toLowerCase() === v || s.isoCode.toLowerCase() === v) || null;
};

/** The state's full name; a legacy ISO code is converted, anything else kept. */
export const stateName = (countryName, value) => findState(countryName, value)?.name || value || '';

export const countryOptions = () => countries().map(c => ({ value: c.name, label: c.name }));

export const stateOptions = (countryName) =>
    statesOf(countryName).map(s => ({ value: s.name, label: s.name }));

export const cityOptions = (countryName, state) => {
    const cIso = countryIso(countryName || DEFAULT_COUNTRY);
    const st = findState(countryName, state);
    if (!cIso || !st) return [];
    return City.getCitiesOfState(cIso, st.isoCode).map(c => ({ value: c.name, label: c.name }));
};

/** The state's ISO code — for the onboarding screens, which still store codes. */
export const stateCode = (countryName, value) => findState(countryName, value)?.isoCode || value || '';
